import { type Ref, computed, ref, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { openModal } from '@ds/components/ModalBase'

import { productToComparison } from '../helpers/recommendationHelper'
import type {
  ComparisonProduct,
  Product,
  ProductSpecification,
} from '../models/product'

const MAX_COMPARED_PRODUCT_SM = 2
const MAX_COMPARED_PRODUCT_LG = 4

export function useProductComparison({
  pageProducts,
}: {
  pageProducts: Ref<Product[]>
}) {
  const products = ref<ComparisonProduct[]>([])
  const productsSpecifications = ref<ProductSpecification[][]>([])
  const productsSpecificationsLoading = ref<boolean>(false)
  const breakpoint = useClientOnlyComputedBreakpoint()
  const isResponsiveComparisonEnabled = ref<boolean>(false)

  const isMobile = computed(() => breakpoint.value < 3)

  const maxComparedProductsLength = computed(() =>
    isMobile.value ? MAX_COMPARED_PRODUCT_SM : MAX_COMPARED_PRODUCT_LG,
  )

  const isAddProductDisabled = computed(
    () => products.value.length >= maxComparedProductsLength.value,
  )

  const { trackComparisonAdd } = useTracking()

  function isProductCompared(product: ComparisonProduct) {
    return products.value.find((i) => i.listingId === product.listingId)
  }

  function removeProduct(product: ComparisonProduct) {
    products.value = products.value.filter(
      (i) => i.listingId !== product.listingId,
    )
  }

  function toggleResponsiveComparison(value: boolean) {
    isResponsiveComparisonEnabled.value = value
  }

  function addProduct(product: ComparisonProduct) {
    if (isAddProductDisabled.value) return
    products.value.push(product)
    trackComparisonAdd({
      averageRate: product.reviewRating?.average ?? '',
      product_name: product.title,
      product_brand: product.brand ?? '',
      product_category: product.category ?? '',
      product_id: product.id ?? '',
      product_model: product.model ?? '',
      product_parent_title: product.title,
      product_price: product.price.amount,
      product_price_currency: product.price.currency,
    })
  }

  function toggleProductSelectionById(productId: string) {
    const product = pageProducts.value.find((p) => p.listingId === productId)
    if (!product) return
    const comparisonProduct = productToComparison(product)
    if (isProductCompared(comparisonProduct)) removeProduct(comparisonProduct)
    else addProduct(comparisonProduct)
  }

  function clear() {
    products.value = []
    productsSpecifications.value = []
  }

  function extractProductId(id?: string) {
    return id?.split('_')[0] ?? ''
  }

  const $httpFetch = useHttpFetch()

  async function fetchProductsSpecifications() {
    productsSpecificationsLoading.value = true
    try {
      const productsSpecificationsRes = await Promise.allSettled(
        products.value.map((product) =>
          $httpFetch(
            '/product-page/products/:productId/technical_specifications',
            {
              pathParams: { productId: extractProductId(product.id) },
            },
          ),
        ),
      )
      productsSpecifications.value = productsSpecificationsRes.map((i) => {
        return i.status === 'fulfilled'
          ? JSON.parse(JSON.stringify(i.value?.specifications || []))
          : []
      })
    } finally {
      productsSpecificationsLoading.value = false
    }
  }

  function openFullModal() {
    void fetchProductsSpecifications()
    openModal('COMPARISON_MODAL')
  }

  function removeExtraProducts() {
    products.value = products.value.slice(0, maxComparedProductsLength.value)
    productsSpecifications.value = productsSpecifications.value.slice(
      0,
      maxComparedProductsLength.value,
    )
  }

  watch(breakpoint, () => {
    // When resize, slice products list to max
    removeExtraProducts()
    // Activate mobile comparison when switch from desktop to mobile with compared product to prevent side effect
    if (products.value.length > 0 && isMobile.value)
      isResponsiveComparisonEnabled.value = true
  })

  watch(isResponsiveComparisonEnabled, (value) => {
    if (!value) clear()
  })

  return {
    products,
    toggleProductSelectionById,
    isResponsiveComparisonEnabled,
    isAddProductDisabled,
    clear,
    removeProduct,
    maxComparedProductsLength,
    openFullModal,
    productsSpecifications,
    fetchProductsSpecifications,
    productsSpecificationsLoading,
    isProductCompared,
    toggleResponsiveComparison,
    addProduct,
  }
}
